"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Trophy, Users, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Hlasovanie } from "@/types/database";
import dynamic from "next/dynamic";
import { AktualizacieSection } from "./aktualizacie-section";
import { KomunitneSekcie } from "./komunitne-sekcie";
import { AnalyzaTimu } from "./analyza-timu";

// Lazy loaded Motion pre zníženie bundle size
const MotionDiv = dynamic(
  () => import("framer-motion").then((mod) => ({ default: mod.motion.div })),
  {
    ssr: false,
    loading: () => (
      <div className={cn("max-w-4xl")}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted/20 rounded-full w-48 mb-8" />
          <div className="h-20 bg-muted/20 rounded-2xl mb-12" />
          <div className="h-14 bg-muted/20 rounded-full w-64" />
        </div>
      </div>
    ),
  },
);

const HERO_BADGE_CLASSES = cn(
  "mb-4 px-3 py-1 text-sm font-medium",
  "border-primary/20 bg-primary/5 text-primary",
);
const HERO_TITLE_CLASSES = cn(
  "text-5xl lg:text-8xl font-black tracking-tighter mb-8 leading-[0.95] uppercase",
  "bg-linear-to-r from-foreground to-foreground/70 bg-clip-text text-foreground drop-shadow-sm min-h-[1.2em]",
);
const CTA_CONTAINER_CLASSES =
  "flex flex-wrap gap-6 items-center justify-center sm:justify-start";
const PRIMARY_CTA_CLASSES = cn(
  "h-14 px-10 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95",
  "bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs",
);
const SECONDARY_CTA_CLASSES = cn(
  "h-14 px-10 rounded-full border-muted/20 bg-background/50 backdrop-blur-sm",
  "hover:bg-muted/30 transition-all font-black uppercase tracking-widest text-xs hover:scale-105 active:scale-95",
);
const FEATURE_CARD_CLASSES = cn(
  "border-muted/20 shadow-sm hover:shadow-xl transition-all h-full text-center rounded-3xl overflow-hidden",
  "bg-background/40 backdrop-blur-md group hover:bg-background/60 duration-500",
);

export function HomeKlient({
  aktivneHlasovania,
}: {
  aktivneHlasovania: Hlasovanie[];
}) {
  const SITE_TITLE_BADGE = "Zimné olympijské hry 2026 • Miláno";
  const HERO_TITLE_PART1 = "Hlasujte za";
  const HERO_TITLE_PART2 = "olympijskú nomináciu";
  const HERO_DESC =
    "Pomôžte vybrať najlepšiu zostavu pre slovenský hokejový tím na ZOH 2026. Váš hlas vyjadruje názor fanúšikov a pomáha určiť favoritov národa.";
  const RESULTS_CTA = "Zobraziť výsledky";
  const NO_VOTING_CTA = "Žiadne aktívne hlasovanie";
  const TOURNAMENT_CTA = "Prezrieť turnaj";
  const FEATURES_BADGE = "Fanúšikovská voľba";
  const FEATURES_TITLE = "Váš hlas má váhu";
  const FEATURES_DESC =
    "Každý hlas prispieva k vytvoreniu autentického obrazu toho, koho by fanúšikovia chceli vidieť v olympijskom tíme.";
  const CTA_BADGE = "Zapojte sa";
  const CTA_TITLE = "Buďte súčasťou histórie";
  const CTA_DESC =
    "Vaša účasť pomáha formovať budúcnosť slovenského hokeja. Každý hlas je dôležitý a každý názor sa počíta.";

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section - Landing Style */}
      <section className="relative overflow-hidden border-b border-muted/20 bg-linear-to-b from-muted/30 to-background py-16 md:py-24 lg:py-32">
        <div
          className="absolute inset-0 bg-[url('/hero-pattern.svg')] opacity-[0.02] dark:opacity-[0.04]"
          style={{ backgroundImage: "url(/hero-pattern.svg)" }}
        />
        <div className="container mx-auto relative px-4 sm:px-6 lg:px-8">
          <MotionDiv
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <Badge
              variant="outline"
              className={cn(
                HERO_BADGE_CLASSES,
                "rounded-full px-4 py-1.5 border-primary/30 shadow-inner",
              )}
            >
              {SITE_TITLE_BADGE}
            </Badge>
            <h1 className={HERO_TITLE_CLASSES}>
              {HERO_TITLE_PART1}{" "}
              <span className="text-primary drop-shadow-[0_0_15px_rgba(var(--primary-rgb),0.3)]">
                {HERO_TITLE_PART2}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground/90 mb-12 leading-relaxed max-w-2xl font-medium">
              {HERO_DESC}
            </p>

            <div className={CTA_CONTAINER_CLASSES}>
              {aktivneHlasovania.length > 0 ? (
                <Button size="lg" className={PRIMARY_CTA_CLASSES} asChild>
                  <Link
                    href="/vysledky"
                    aria-label="Zobraziť výsledky hlasovania"
                  >
                    <BarChart3 className="h-5 w-5 mr-3" aria-hidden="true" />
                    {RESULTS_CTA}
                  </Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  disabled
                  className="h-14 px-10 rounded-full bg-muted/50 text-muted-foreground font-black uppercase tracking-widest text-xs border border-muted/20"
                >
                  {NO_VOTING_CTA}
                </Button>
              )}
              <Button
                asChild
                variant="outline"
                size="lg"
                className={SECONDARY_CTA_CLASSES}
              >
                <Link href="/skupiny" aria-label="Prezrieť turnajové skupiny">
                  <Trophy
                    className="h-5 w-5 mr-3 text-primary"
                    aria-hidden="true"
                  />
                  {TOURNAMENT_CTA}
                </Link>
              </Button>
            </div>
          </MotionDiv>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-muted/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20 max-w-3xl mx-auto space-y-4"
          >
            <Badge
              variant="outline"
              className="mb-4 px-4 py-1.5 bg-primary/5 text-primary border-primary/20 rounded-full text-xs font-black uppercase tracking-widest"
            >
              {FEATURES_BADGE}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter uppercase drop-shadow-sm">
              {FEATURES_TITLE}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground/90 leading-relaxed font-medium">
              {FEATURES_DESC}
            </p>
          </MotionDiv>

          <MotionDiv
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                icon: <Trophy className="h-7 w-7" />,
                title: "Olympijská nominácia",
                desc: "Hlasovanie o zostavu pre ZOH 2026 v Miláne",
                color:
                  "bg-status-warning/10 text-status-warning shadow-status-warning/10",
              },
              {
                icon: <Users className="h-7 w-7" />,
                title: "Fanúšikovská komunita",
                desc: "Spojenie všetkých slovenských fanúšikov hokeja",
                color:
                  "bg-status-info/10 text-status-info shadow-status-info/10",
              },
              {
                icon: <BarChart3 className="h-7 w-7" />,
                title: "Transparentné výsledky",
                desc: "Všetky hlasy sú verejné a overiteľné",
                color:
                  "bg-status-success/10 text-status-success shadow-status-success/10",
              },
              {
                icon: <Target className="h-7 w-7" />,
                title: "Strategický výber",
                desc: "Vyvážený tím pre maximálny úspech",
                color: "bg-primary/10 text-primary shadow-primary/10",
              },
            ].map((itemInfo, i) => (
              <MotionDiv key={i} variants={item}>
                <Card className={FEATURE_CARD_CLASSES}>
                  <CardHeader className="pb-6 pt-10">
                    <div
                      className={cn(
                        "w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-lg",
                        itemInfo.color,
                      )}
                    >
                      {itemInfo.icon}
                    </div>
                    <CardTitle className="text-xl font-black tracking-tight uppercase">
                      {itemInfo.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-10">
                    <p className="text-sm text-muted-foreground/80 leading-relaxed font-bold uppercase tracking-wider">
                      {itemInfo.desc}
                    </p>
                  </CardContent>
                </Card>
              </MotionDiv>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* SEO Freshness Boost */}
      <section className="py-12 bg-muted/5 border-y border-muted/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Aktuálna nominácia Slovenska na hokejový turnaj Miláno 2026
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Dôležité zmeny v kádri: Zranenie Mareka Hrivíka znamenalo
              prerušenie jeho účasti na olympijskej príprave, čo otvorilo
              priestor pre návrat Lukáša Cingela do nominácie. Táto zmena z
              28.1.2026 prináša nové možnosti pre ofenzívnu zostavu Slovenska.
              Cingel so skúsenosťami z medzinárodných turnajov posilní
              konkurenciu v útočných formáciách, zatiaľ čo Hrivík pracuje na
              návrate do plnej kondície. Fanúšikovia môžu teraz hlasovať o
              aktualizovanej súpiske, ktorá odráža aktuálnu situáciu v tíme pred
              ZOH 2026.
            </p>
          </div>
        </div>
      </section>

      {/* Komunitné sekcie */}
      <KomunitneSekcie />

      {/* Aktualizácie Section */}
      <AktualizacieSection />

      {/* Call to Action Section */}
      <section className="py-20 md:py-32 border-t border-muted/20 bg-linear-to-b from-background to-muted/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto space-y-6"
          >
            <Badge
              variant="outline"
              className="mb-4 px-4 py-1.5 bg-primary/5 text-primary border-primary/20 rounded-full text-xs font-black uppercase tracking-widest"
            >
              {CTA_BADGE}
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter uppercase drop-shadow-sm">
              {CTA_TITLE}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground/90 mb-12 leading-relaxed font-medium">
              {CTA_DESC}
            </p>

            <div className={cn(CTA_CONTAINER_CLASSES, "justify-center gap-6")}>
              {aktivneHlasovania.length > 0 ? (
                <Button size="lg" className={PRIMARY_CTA_CLASSES} asChild>
                  <Link
                    href="/vysledky"
                    aria-label="Zobraziť výsledky hlasovania"
                  >
                    <BarChart3 className="h-5 w-5 mr-3" aria-hidden="true" />
                    {RESULTS_CTA}
                  </Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  disabled
                  className="h-14 px-10 rounded-full bg-muted/50 text-muted-foreground font-black uppercase tracking-widest text-xs border border-muted/20"
                >
                  {NO_VOTING_CTA}
                </Button>
              )}
              <Button
                asChild
                variant="outline"
                size="lg"
                className={SECONDARY_CTA_CLASSES}
              >
                <Link href="/skupiny" aria-label="Prezrieť turnajové skupiny">
                  <Trophy
                    className="h-5 w-5 mr-3 text-primary"
                    aria-hidden="true"
                  />
                  {TOURNAMENT_CTA}
                </Link>
              </Button>
            </div>
          </MotionDiv>
        </div>
      </section>

      {/* Analýza tímu */}
      <AnalyzaTimu />
    </div>
  );
}
